import { apiAgent } from "@/network/axiosAgent";
import type { MarketingAssetEntityType } from "./marketingAssets.query-keys";
import {
  mapApiMarketingAsset,
  type SwivuMarketingAssetListApiResponse,
  type SwivuMarketingAssetListPage,
} from "./marketingAssets.types";

export interface GetMarketingAssetListParams {
  boardId: number;
  task?: number;
  resource?: number;
  client?: number;
  limit?: number;
  offset?: number;
}

/**
 * Fetch one page of marketing assets for a board.
 * Optionally filter by the associated file's task / resource / client.
 */
export async function getMarketingAssetList(
  params: GetMarketingAssetListParams,
): Promise<SwivuMarketingAssetListPage> {
  const { data } = await apiAgent.get<SwivuMarketingAssetListApiResponse>(
    `/promote/board/${params.boardId}/marketing-assets`,
    {
      params: {
        ...(params.task !== undefined && { task: params.task }),
        ...(params.resource !== undefined && { resource: params.resource }),
        ...(params.client !== undefined && { client: params.client }),
        limit: params.limit ?? 50,
        offset: params.offset ?? 0,
      },
    },
  );

  return {
    count: data.count,
    results: data.results.map(mapApiMarketingAsset),
  };
}

/** Fetch every page for an entity (limit capped at 100 by the API). */
export async function getAllMarketingAssetsForEntity(params: {
  boardId: number;
  entityType: MarketingAssetEntityType;
  entityId: number;
}): Promise<SwivuMarketingAssetListPage> {
  const limit = 100;
  let offset = 0;
  const results: Awaited<ReturnType<typeof getMarketingAssetList>>["results"] = [];
  let count = 0;

  while (true) {
    const page = await getMarketingAssetList({
      boardId: params.boardId,
      [params.entityType]: params.entityId,
      limit,
      offset,
    });
    count = page.count;
    results.push(...page.results);
    offset += limit;
    if (offset >= count || page.results.length === 0) break;
  }

  return { count, results };
}
